<template>
    <card>
        <template #header>
            <h1 class="card-title mb-0">Positive ID Events</h1>
            <p class="text-muted small mb-0">
                Inspection report for board audits. Ohio OAC 4729:5-5-04 requires positive-identification
                records be readily retrievable for three years.<br />
                <strong>Target mapping</strong>: Rx Entry and Dispense events target the
                <em>Patient</em> (no Rx PK exists at entry; new orders have no Id at save time),
                shown as the numeric patient id. Pharmacist Verification and Annotation events
                target the <em>Prescription</em>, shown as <code>StoreID-RxNumber-RfNumber</code>.
                Compounding targets the <em>Compound Batch</em>. To trace one Rx end-to-end, query
                by patient around the entry/dispense timestamps, then by the prescription key for
                downstream events.
            </p>
        </template>
        <b-alert :show="!!warning" variant="warning" dismissible @dismissed="warning = ''">{{ warning }}</b-alert>
        <div class="filter-grid mb-2">
            <DateRangeFilter v-model="dateRange" label="Date Range" :clearable="false" @input="resetAndReload" />
            <fg-input label="User">
                <b-form-input v-model="filters.userName" debounce="400" placeholder="Username (partial match)"
                              @update="resetAndReload" />
            </fg-input>
            <fg-input label="Action">
                <b-form-select v-model="filters.actionType" :options="actionTypeOptions" @change="resetAndReload" />
            </fg-input>
            <fg-input label="Target Entity">
                <b-form-select v-model="filters.targetEntityType" :options="targetEntityTypeOptions"
                               @change="resetAndReload" />
            </fg-input>
            <fg-input label="Target ID">
                <b-form-input v-model.trim="filters.targetEntityId" debounce="400"
                              placeholder="e.g. 5-1234567-0 or 12345" @update="resetAndReload" />
            </fg-input>
            <fg-input label="Method">
                <b-form-select v-model="filters.method" :options="methodOptions" @change="resetAndReload" />
            </fg-input>
            <div class="d-flex align-items-end">
                <b-button variant="outline-secondary" size="sm" class="mb-3 mr-2" @click="clearFilters">Clear</b-button>
                <b-dropdown variant="success" size="sm" class="mb-3" text="Export...">
                    <b-dropdown-item @click="savePDF">
                        <i class="fa fa-fw fa-lg fa-file-pdf" /> PDF
                    </b-dropdown-item>
                    <b-dropdown-item @click="saveCSV">
                        <i class="fa fa-fw fa-lg fa-file-csv" /> CSV
                    </b-dropdown-item>
                </b-dropdown>
            </div>
        </div>
        <b-table id="positive-id-events-table"
                 ref="positive-id-events-table"
                 outlined
                 show-empty
                 empty-text="No positive-ID events match the selected filters."
                 :busy="loading"
                 :fields="fields"
                 :items="rows">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle mr-2"></b-spinner><strong>Loading...</strong>
                </div>
            </template>
            <template #cell(actionPerformedUtc)="row">{{ formatDate(row.item.actionPerformedUtc) }}</template>
            <template #cell(actionType)="row">{{ formatActionType(row.item.actionType) }}</template>
            <template #cell(targetEntityType)="row">{{ formatTargetEntityType(row.item.targetEntityType) }}</template>
            <template #cell(method)="row">{{ formatMethod(row.item.method) }}</template>
        </b-table>
        <BottomPagerBar :value="pageNumber" :total-rows="totalCount" :per-page="pageSize" show-total
                        show-page-size-selector item-name="event" :loading="loading" no-stick
                        @input="onPageChange" @change-page-size="onPageSizeChange">
            <template #left-after>
                <b-button @click="reload" variant="primary" size="sm" class="m-0">
                    <b-icon-arrow-clockwise /> Refresh
                </b-button>
            </template>
        </BottomPagerBar>
    </card>
</template>
<script lang="ts">
import { Component, Mixins } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import moment from "moment/moment";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import DateRangeFilter from "@/components/DateRangeFilter.vue";
import { ReportExporter } from "@/mixins/ReportExporter";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import PositiveIdService, {
    PositiveIdActionType,
    PositiveIdEventRow,
    PositiveIdMethod,
    PositiveIdTargetEntityType,
} from "@/services/PositiveIdService";

const ACTION_TYPE_LABELS: Record<number, string> = {
    [PositiveIdActionType.RxEntry]: "Rx Entry",
    [PositiveIdActionType.RphVerification]: "Pharmacist Verification",
    [PositiveIdActionType.Dur]: "DUR",
    [PositiveIdActionType.Dispense]: "Dispense",
    [PositiveIdActionType.Compound]: "Compound",
    [PositiveIdActionType.ImmunizationAdmin]: "Immunization Admin",
    [PositiveIdActionType.InjectableAdmin]: "Injectable Admin",
    [PositiveIdActionType.Transcribe]: "Transcribe",
    [PositiveIdActionType.RxAnnotate]: "Rx Annotation",
    [PositiveIdActionType.Enrollment]: "Enrollment",
};

const TARGET_ENTITY_TYPE_LABELS: Record<number, string> = {
    [PositiveIdTargetEntityType.Prescription]: "Prescription",
    [PositiveIdTargetEntityType.Order]: "Order",
    [PositiveIdTargetEntityType.CompoundBatch]: "Compound Batch",
    [PositiveIdTargetEntityType.User]: "User",
    [PositiveIdTargetEntityType.Patient]: "Patient",
};

const METHOD_LABELS: Record<number, string> = {
    [PositiveIdMethod.BadgeCard]: "Badge Card",
    [PositiveIdMethod.BarCode]: "Bar Code",
    [PositiveIdMethod.Biometric]: "Biometric",
    [PositiveIdMethod.ProximityBadge]: "Proximity Badge",
};

@Component({
    name: "PositiveIdInspectionPage",
    components: { BottomPagerBar, DateRangeFilter },
})
export default class PositiveIdInspectionPage extends Mixins(ReportExporter) {
    dateRange: { startDate: any, endDate: any } = {
        startDate: moment().subtract(30, "days").startOf("day").toDate(),
        endDate: moment().endOf("day").toDate(),
    };
    filters = {
        userName: "",
        actionType: null as PositiveIdActionType | null,
        targetEntityType: null as PositiveIdTargetEntityType | null,
        targetEntityId: null as string | null,
        method: null as PositiveIdMethod | null,
    };
    rows: PositiveIdEventRow[] = [];
    loading = false;
    warning = "";
    pageNumber = 1;
    pageSize = 50;
    totalCount = 0;

    private abortController: AbortController | null = null;

    mounted() { this.reload(); }

    get actionTypeOptions() {
        const opts = Object.entries(ACTION_TYPE_LABELS).map(([v, t]) => ({ value: Number(v), text: t }));
        return [{ value: null, text: "All actions" }, ...opts];
    }

    get targetEntityTypeOptions() {
        const opts = Object.entries(TARGET_ENTITY_TYPE_LABELS).map(([v, t]) => ({ value: Number(v), text: t }));
        return [{ value: null, text: "All targets" }, ...opts];
    }

    get methodOptions() {
        const opts = Object.entries(METHOD_LABELS).map(([v, t]) => ({ value: Number(v), text: t }));
        return [{ value: null, text: "All methods" }, ...opts];
    }

    get fields(): BvTableFieldArray {
        return [
            { key: "actionPerformedUtc", label: "When", sortable: false, formatter: this.formatDate },
            { key: "userName", label: "User" },
            { key: "actionType", label: "Action", formatter: this.formatActionType },
            { key: "targetEntityType", label: "Target", formatter: this.formatTargetEntityType },
            { key: "targetEntityId", label: "Target ID" },
            { key: "method", label: "Method", formatter: this.formatMethod },
            { key: "correlationId", label: "Correlation" },
        ];
    }

    resetAndReload() {
        this.pageNumber = 1;
        return this.reload();
    }

    onPageChange(newPage: number) {
        this.pageNumber = newPage;
        this.reload();
    }

    onPageSizeChange(newSize: number) {
        this.pageSize = newSize;
        this.pageNumber = 1;
        this.reload();
    }

    async reload() {
        this.loading = true;
        this.warning = "";
        this.abortController?.abort();
        this.abortController = new AbortController();

        const fromUtc = moment(this.dateRange.startDate).startOf("day").toISOString();
        const toUtc = moment(this.dateRange.endDate).endOf("day").toISOString();

        try {
            const result = await PositiveIdService.searchEvents({
                userName: this.filters.userName || null,
                actionType: this.filters.actionType,
                targetEntityType: this.filters.targetEntityType,
                targetEntityId: this.filters.targetEntityId || null,
                method: this.filters.method,
                fromUtc,
                toUtc,
                offset: (this.pageNumber - 1) * this.pageSize,
                limit: this.pageSize,
            }, this.abortController.signal);
            this.rows = result.rows ?? [];
            this.totalCount = result.totalCount ?? 0;
        } catch (err: any) {
            if (err.name === "CanceledError" || err.name === "AbortError") return;
            this.warning = err?.response?.data ?? "Failed to load positive-ID events.";
            this.rows = [];
            this.totalCount = 0;
        } finally {
            this.loading = false;
        }
    }

    clearFilters() {
        this.filters = {
            userName: "",
            actionType: null,
            targetEntityType: null,
            targetEntityId: null,
            method: null,
        };
        this.pageNumber = 1;
        this.reload();
    }

    async savePDF() {
        try {
            const all = await this.fetchAllForExport();
            if (all.length === 0) {
                this.$notification(NotificationOptions.warning("No events match the current filters."));
                return;
            }
            await this.exportPdfFromRows("Positive ID Events", all);
        } catch (err: any) {
            this.warning = err?.response?.data ?? "Failed to build PDF export.";
        }
    }

    async saveCSV() {
        try {
            const all = await this.fetchAllForExport();
            if (all.length === 0) {
                this.$notification(NotificationOptions.warning("No events match the current filters."));
                return;
            }
            this.ExportCSV("Positive ID Events", all, this.fields);
        } catch (err: any) {
            this.warning = err?.response?.data ?? "Failed to build CSV export.";
        }
    }

    private async fetchAllForExport(): Promise<PositiveIdEventRow[]> {
        const fromUtc = moment(this.dateRange.startDate).startOf("day").toISOString();
        const toUtc = moment(this.dateRange.endDate).endOf("day").toISOString();
        const baseFilters = {
            userName: this.filters.userName || null,
            actionType: this.filters.actionType,
            targetEntityType: this.filters.targetEntityType,
            targetEntityId: this.filters.targetEntityId || null,
            method: this.filters.method,
            fromUtc,
            toUtc,
        };
        const PAGE = 1000;  // matches the search SP's hard cap
        const all: PositiveIdEventRow[] = [];
        let offset = 0;
        while (true) {
            const result = await PositiveIdService.searchEvents({ ...baseFilters, offset, limit: PAGE });
            const rows = result.rows ?? [];
            all.push(...rows);
            if (rows.length < PAGE) break;  // last page
            offset += PAGE;
            if (result.totalCount > 0 && all.length >= result.totalCount) break;
        }
        return all;
    }

    private async exportPdfFromRows(reportName: string, items: PositiveIdEventRow[]): Promise<void> {
        const jspdf = await import(/* webpackChunkName: "jspdf" */ "jspdf");
        const autotable = await import(/* webpackChunkName: "jspdf" */ "jspdf-autotable");
        const doc = new jspdf.default();
        const head = [this.fields.map((f: any) => f.label)];
        const body = items.map(item =>
            this.fields.map((field: any) => {
                const raw = (item as any)[field.key];
                return field.formatter ? field.formatter(raw) : (raw ?? "");
            }),
        );
        autotable.default(doc, { head, body });
        doc.save(`${reportName}.pdf`);
    }

    formatDate(d: string | null): string { return d ? moment(d).format("L LT") : ""; }
    formatActionType(v: number): string { return ACTION_TYPE_LABELS[v] ?? `#${v}`; }
    formatTargetEntityType(v: number): string { return TARGET_ENTITY_TYPE_LABELS[v] ?? `#${v}`; }
    formatMethod(v: number): string { return METHOD_LABELS[v] ?? `#${v}`; }
}
</script>
<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 330px), 1fr));
}
</style>
